// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Rendering a failed request.
 *
 * The server's `message` is already localized (§14) and is shown as-is. The `request_id` is
 * shown too — it is what turns "it broke" into something findable in the logs, and asking a
 * user to reproduce a problem is far more expensive than printing sixteen characters.
 *
 * `details` are rendered when they locate something the user can act on: the empty slots of
 * a rejected filing, the offending line of a rejected event.
 */

import { useTranslation } from "react-i18next";
import { ApiError } from "@/api/client";

/** `2026-10-29 01:36:00 UTC`: both clocks in one zone, so they can be compared at a glance. */
function utcText(value: unknown): string {
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return `${date.toISOString().slice(0, 19).replace("T", " ")} UTC`;
}

/** A time as the operator reads it, on their own clock: `2026/11/3 14:30:12`. */
function localText(value: unknown): string {
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

export function ErrorBanner({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;

  if (!(error instanceof ApiError)) {
    return (
      <div className="banner banner-error" role="alert">
        {t("error.network")}
      </div>
    );
  }

  const missing = error.details.missing_position_codes;
  const lines = error.details.lines;
  const fields = error.details.fields;
  const mismatches = error.details.mismatches;

  return (
    <div className="banner banner-error" role="alert">
      <strong>{error.localizedMessage}</strong>
      {Array.isArray(missing) && missing.length > 0 && (
        <ul className="banner-detail">
          {missing.slice(0, 20).map((code) => (
            <li key={String(code)}>{String(code)}</li>
          ))}
          {missing.length > 20 && <li>… {missing.length - 20}</li>}
        </ul>
      )}
      {/* A 422 from the framework says which field and why, and the top-line message
          cannot: it is one sentence for every malformed body there will ever be. Without
          this the user is told "the request body is malformed" about a form with eight
          fields in it. */}
      {Array.isArray(fields) && fields.length > 0 && (
        <ul className="banner-detail">
          {fields.map((entry, index) => {
            const { field, reason } = (entry ?? {}) as { field?: string; reason?: string };
            // `body.archive_prefix` → `archive_prefix`: the envelope's path prefix is for
            // whoever reads the log, not for the person looking at the form.
            const name = String(field ?? "").replace(/^body\./, "");
            return (
              <li key={index}>
                <span className="mono">{name}</span>
                {reason ? ` — ${t(`error.field.${reason}`, { defaultValue: reason })}` : ""}
              </li>
            );
          })}
        </ul>
      )}
      {/* Both clocks, so the operator sees which way and how far theirs is off before
          setting it right — the SNs a filing is given are dated by it. */}
      {error.code === "CLIENT_CLOCK_SKEW" && (
        <p className="banner-detail">
          {t("error.clockSkew", {
            client: utcText(error.details.client_time),
            server: utcText(error.details.server_time),
            minutes: Math.max(1, Math.round(Math.abs(Number(error.details.skew_seconds)) / 60)),
          })}
        </p>
      )}
      {/* A change dated out of range: which bound, and against what, so the operator knows
          whether to move the time or to set their computer's clock right. */}
      {error.code === "MAINTENANCE_TIME_OUT_OF_RANGE" &&
        error.details.reason === "before_archive_created" && (
          <p className="banner-detail">
            {t("error.timeBeforeFiling", {
              occurred: localText(error.details.occurred_at),
              archive: String(error.details.archive_no ?? ""),
              created: localText(error.details.created_at),
            })}
          </p>
        )}
      {error.code === "MAINTENANCE_TIME_OUT_OF_RANGE" &&
        error.details.reason === "too_far_in_future" && (
          <p className="banner-detail">
            {t("error.timeInFuture", {
              occurred: localText(error.details.occurred_at),
              server: localText(error.details.server_time),
              minutes: Math.max(
                1,
                Math.round(
                  (Date.parse(String(error.details.occurred_at)) -
                    Date.parse(String(error.details.server_time))) /
                    60000,
                ),
              ),
            })}
          </p>
        )}
      {/* SERIAL_FORMAT_INVALID: which slot, which serial, and the rules it failed. */}
      {Array.isArray(mismatches) && mismatches.length > 0 && (
        <ul className="banner-detail">
          {mismatches.map((entry, index) => {
            const { position_code, serial, material_code, patterns } = (entry ?? {}) as {
              position_code?: string;
              serial?: string;
              material_code?: string;
              patterns?: string[];
            };
            return (
              <li key={index}>
                {t("error.serialFormat", {
                  position: position_code ?? "",
                  serial: serial ?? "",
                  material: material_code ?? "",
                  patterns: (patterns ?? []).join(" / "),
                })}
              </li>
            );
          })}
        </ul>
      )}
      {Array.isArray(lines) && lines.length > 0 && (
        <ul className="banner-detail">
          {lines.map((line, index) => (
            <li key={index}>{JSON.stringify(line)}</li>
          ))}
        </ul>
      )}
      {error.requestId && (
        <small className="banner-request-id">{t("error.requestId", { id: error.requestId })}</small>
      )}
    </div>
  );
}
